import { AsyncPipe } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, Input, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Data, Event, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CONTENT_LAYOUT_CONTAINER_ID, ImproverConstants, MAIN_DRAWER_CONTAINER_ID, MAIN_MENU_CONTAINER_ID } from '@improver/app-config';
import { ChatbotWrapperComponent } from '@improver/chat/chat-shared';
import { DrawerContainerDirective } from '@improver/components/drawer';
import { ModalsService, ModalWidth } from '@improver/components/modals';
import { NotificationService, rejectFalsy } from '@improver/core';
import { SecurityService, SessionService } from '@improver/infrastructure/auth-data-layer';
import { InstanceThemeDirective, ResponsiveService } from '@improver/infrastructure/layout-data-layer';
import { SseService } from '@improver/infrastructure/sse-data-layer';
import { BlockModule } from '@improver/shared-ui/block';
import { ImproverLayoutService } from '@improver/shared-ui/layout';
import { DesktopClassDirective, DesktopDirective, MobileClassDirective } from '@improver/shared-ui/responsive';
import { CHATBOT_ACCESS, CHATBOT_AGENT_ACCESS, CHATBOT_PUBLICATION_ACCESS } from '@improver/shared/permissions';
import { SettingsService } from '@improver/shared/ui/features/settings/settings.service';
import { get } from 'lodash-es';
import { Observable, of, share, take } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ExtendedSessionModalComponent } from '../extend-session-modal/extend-session-modal.component';
import { ImproverLayoutGeneralIssueService } from './improver-layout-general-issue.service';
import { ImproverMobileBannerComponent } from '../improver-mobile-banner/improver-mobile-banner.component';
import { ImproverNavbarComponent } from '../improver-navbar/improver-navbar.component';
import { ImproverSidebarComponent } from '../improver-sidebar';
import { MaintenanceGeneralIssueComponent } from '../maintenance-general-issue/maintenance-general-issue.component';

interface ToastNotificationData {
  type: 'warning' | 'info';
  title: string;
  message: string;
}

@Component({
  selector: 'lay-improver-layout',
  templateUrl: './improver-layout.component.html',
  styleUrls: ['./improver-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    AsyncPipe,
    BlockModule,
    ChatbotWrapperComponent,
    DesktopClassDirective,
    DesktopDirective,
    DrawerContainerDirective,
    ImproverNavbarComponent,
    ImproverSidebarComponent,
    InstanceThemeDirective,
    MobileClassDirective,
    RouterOutlet,
    ImproverMobileBannerComponent,
    MaintenanceGeneralIssueComponent,
  ],
})
export class ImproverLayoutComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly settingsService = inject(SettingsService);
  private readonly generalIssueService = inject(ImproverLayoutGeneralIssueService);
  private readonly securityService = inject(SecurityService);
  private readonly improverLayoutService = inject(ImproverLayoutService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly responsiveService = inject(ResponsiveService);
  private readonly router = inject(Router);
  private readonly sseService = inject(SseService);
  private readonly notificationService = inject(NotificationService);
  private readonly sessionService = inject(SessionService);
  private readonly modalsService = inject(ModalsService);

  @Input() waitForPermissions = false;
  @Input() navbarActionsVisible = true;
  @Input() offcanvasMenuVisible = true;
  @Input() sidebarVisible = true;
  @Input() navbarVisible = true;
  @Input() navbarLogoLink = '/';
  @Input() noScroll = false;
  @Input() noLayoutContentScroll = false;
  permissionLoaded: Observable<boolean>;
  readonly blockUiName: string = ImproverConstants.GLOBAL_BLOCK_UI_NAME;
  readonly mainDrawerId: string = MAIN_DRAWER_CONTAINER_ID;
  readonly contentLayoutContainerId: string = CONTENT_LAYOUT_CONTAINER_ID;
  readonly sidebarContainerId: string = MAIN_MENU_CONTAINER_ID;
  readonly isSidebarCollapsed = toSignal(this.settingsService.isAsideSidebarCollapsed());
  readonly isMobileBannerDismissed = signal(false);
  readonly isDashboardView = signal(false);
  readonly isMobileView = toSignal(this.responsiveService.isMobileObservable());
  readonly isMobileBannerVisible = computed(() => !this.isMobileBannerDismissed() && this.isMobileView() && this.isDashboardView());
  readonly isTestEnvironmentBannerVisible = this.generalIssueService.isTestEnvironmentBannerVisible;
  protected readonly isChatAvailable = toSignal(this.isChatbotAvailable().pipe(share()));
  protected readonly maintenanceMessage = this.generalIssueService.maintenanceMessage;
  protected readonly isMaintenanceMessageVisible = this.generalIssueService.isMaintenanceMessageVisible;
  readonly dynamicHeaderHeight = computed(() => this.calculateDynamicHeaderHeight());
  readonly dynamicHeaderHeightPx = computed(() => `${this.dynamicHeaderHeight()}px`);

  constructor() {
    effect(() => this.improverLayoutService.setMobileBannerVisibility(this.isMobileBannerVisible()));
    effect(() => this.improverLayoutService.setDrawerTopShift(this.dynamicHeaderHeight()));
  }

  ngOnInit() {
    const routeData: Data = this.activatedRoute.snapshot.data;
    const permissionsCheck: boolean = get(routeData, 'waitForPermissions', false);

    this.offcanvasMenuVisible = get(routeData, 'offcanvasMenuVisible', true);

    if (permissionsCheck) {
      this.permissionLoaded = this.securityService.isPermissionLoaded();
    } else {
      this.permissionLoaded = of(true);
    }

    this.isDashboardView.set(this.router.url.includes(ImproverConstants.ROUTES.PERSONNEL_DASHBOARD));

    this.router.events
      .pipe(
        filter((event: Event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response: NavigationEnd) => {
        if (response.url === `/${ImproverConstants.ROUTES.PROFILE}`) {
          this.navbarLogoLink = response.url;
        } else {
          this.navbarLogoLink = get(routeData, 'navbarLogoLink', '/');
        }

        this.isDashboardView.set(this.router.url.includes(ImproverConstants.ROUTES.PERSONNEL_DASHBOARD));
      });

    this.subscribeNavbarActionsVisible().subscribe((navbarActionsVisible: boolean) => {
      this.navbarActionsVisible = get(routeData, 'navbarActionsVisible', navbarActionsVisible);
    });

    this.isSidebarVisible().subscribe((sidebarVisible: boolean) => {
      this.sidebarVisible = get(routeData, 'sidebarVisible', sidebarVisible);
    });

    this.isNavbarVisible().subscribe((navbarVisible: boolean) => {
      this.navbarVisible = get(routeData, 'navbarVisible', navbarVisible);
    });

    this.isNoScroll().subscribe((isNoScroll: boolean) => {
      this.noScroll = get(routeData, 'noScroll', isNoScroll);
    });

    this.isNoLayoutContentScroll().subscribe((isNoLayoutContentScroll: boolean) => {
      this.noLayoutContentScroll = get(routeData, 'noLayoutContentScroll', isNoLayoutContentScroll);
    });

    this.sseService
      .getNotificationsStream('toast_message')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: ToastNotificationData) => this.handleSseToastNotification(data));

    this.sessionService.warning$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.modalsService.open(ExtendedSessionModalComponent, {}, ModalWidth.narrow);
    });
  }

  dismissMobileBanner(isDismissed: boolean) {
    this.isMobileBannerDismissed.set(isDismissed);
  }

  dismissMaintenanceMessage(isDismissed: boolean) {
    this.generalIssueService.dismissMaintenanceMessage(isDismissed);
  }

  private subscribeNavbarActionsVisible(): Observable<boolean> {
    return this.improverLayoutService.areNavbarActionsVisible().pipe(takeUntilDestroyed(this.destroyRef));
  }

  private isSidebarVisible(): Observable<boolean> {
    return this.improverLayoutService.isSidebarVisible().pipe(takeUntilDestroyed(this.destroyRef));
  }

  private isNavbarVisible(): Observable<boolean> {
    return this.improverLayoutService.isNavbarVisible().pipe(takeUntilDestroyed(this.destroyRef));
  }

  private isNoScroll(): Observable<boolean> {
    return this.improverLayoutService.isNoScroll().pipe(takeUntilDestroyed(this.destroyRef));
  }

  private isNoLayoutContentScroll(): Observable<boolean> {
    return this.improverLayoutService.isNoLayoutContentScroll().pipe(takeUntilDestroyed(this.destroyRef));
  }

  private isChatbotAvailable(): Observable<boolean> {
    return this.securityService.isPermissionLoaded().pipe(
      rejectFalsy(),
      map(() => this.securityService.hasAnyPermission([CHATBOT_ACCESS, CHATBOT_PUBLICATION_ACCESS, CHATBOT_AGENT_ACCESS])),
      take(1)
    );
  }

  private calculateDynamicHeaderHeight() {
    const headerHeight = 56;
    const mobileBannerHeight = 40;
    const maintenanceMessageHeight = 40;
    const testEnvironmentBannerHeight = 40;

    let dynamicHeaderHeight = headerHeight;

    if (this.isMobileBannerVisible()) {
      dynamicHeaderHeight += mobileBannerHeight;
    }

    if (this.isMaintenanceMessageVisible()) {
      dynamicHeaderHeight += maintenanceMessageHeight;
    }

    if (this.isTestEnvironmentBannerVisible()) {
      dynamicHeaderHeight += testEnvironmentBannerHeight;
    }

    return dynamicHeaderHeight;
  }

  private handleSseToastNotification({ type, title, message }: ToastNotificationData) {
    if (type === 'warning') {
      this.notificationService.warning(message, title);

      return;
    }

    this.notificationService.info(message, title);
  }
}
